import type React from "react"

import {
  surfaceColor,
  surfaceSize,
  surfaceText
} from "../../constants/surface-tokens"

export { TABULAR } from "../../constants/surface-tokens"

/**
 * Home's design tokens.
 *
 * The palette itself now lives in `constants/surface-tokens`, because the
 * bell and the notification centre have to look like the same product and a
 * copy per surface drifts. What stays here is Home's own layout vocabulary —
 * the grid, the metric order, the card style — and these aliases, which every
 * `DashboardPage/` module already imports by name.
 */
export const homeColor = surfaceColor

export const homeText = surfaceText

export const homeSize = surfaceSize

export const HOME_FONT_STACK = "Inter, Arial, sans-serif"

/**
 * Home's pastel accents. Soft tinted fills with a readable ink of the same
 * hue, used only for the small glyph squares and chips — the cards stay
 * white and the canvas stays its cool grey, so colour marks a kind of thing
 * rather than painting the page. Each ink reads at AA on its own fill.
 */
export const homePastel = {
  mint: { bg: "#E4F5EE", fg: "#1F7A5C" },
  sky: { bg: "#E7F0FD", fg: "#2457A8" },
  lilac: { bg: "#EEEAFB", fg: "#5B3FB5" },
  peach: { bg: "#FDEFE4", fg: "#B35A1F" },
  rose: { bg: "#FCECEE", fg: "#B23B49" },
  teal: { bg: "#E3F3F6", fg: "#1F6B7C" }
} as const

export type HomePastel = keyof typeof homePastel

export const homeCardStyle: React.CSSProperties = {
  background: homeColor.surface,
  border: `1px solid ${homeColor.border}`,
  // A touch rounder than the shared token: Home is the one screen that is
  // mostly cards, and 12px keeps a page of them from reading as a grid of
  // boxes without changing any other surface.
  borderRadius: 12,
  boxShadow:
    "0 1px 2px rgba(37, 51, 72, 0.04), 0 4px 14px rgba(37, 51, 72, 0.04)"
}

/**
 * Three columns once the content area can hold all of them readably, two
 * below that (charts and shortcuts drop under the snapshot), one on a tablet.
 * The thresholds are on the BODY's own width, not the viewport: the sidebar
 * takes 236px of it and a media query cannot see that.
 */
export const HOME_BODY_GRID = "home-body-grid"

/**
 * The default reading order for the metric grid, used only when a layout has
 * no saved order of its own. A saved layout keeps exactly the order it holds.
 */
export const DEFAULT_METRIC_ORDER = [
  "revenue",
  "revenue_in_window",
  "outstanding_receivables",
  "gross_margin",
  "win_rate",
  "active_projects",
  "delayed_projects",
  "pending_approvals",
  "budget_health"
] as const
